import React from 'react';
import { observer } from 'mobx-react';
import { AppServices, Misc } from '@singularsystems/neo-core';
import { Neo, NeoGrid, Views } from '@singularsystems/neo-react';
import * as Roles from '../Models/Security/Roles';
import ResourceRoleTree from "../Components/ResourceRoleTree";
import '../Styles/Authorisation.scss';
import UserGroupsVM, { UserGroupsViewState } from './UserGroupsVM';
import UserGroupLookup from '../Models/UserGroupLookup';
import { IReactionDisposer, reaction } from 'mobx';
import { TextConstants } from '../../Common/TextConstants';
import { Users } from '../../AccessControl/Models/Commands/Security/Roles';

class UserGroupsViewParams {
    public userGroup = {};
    public tab = {};
}

@observer
export default class UserGroupsView extends Views.ViewBase<UserGroupsVM, UserGroupsViewParams> {

    public static params = new UserGroupsViewParams();
    public lastTab = "Members";

    constructor(props: unknown) {
        super("User Groups", UserGroupsVM, props);

        this.setupReaction();
    }

    public tabChangeReactionDisposer?: IReactionDisposer;

    private setupReaction() {
        this.tabChangeReactionDisposer = reaction(() => {
            return {
                selectedTab: this.viewModel.selectedTab,
            }
        }, async (result) => {
            if (this.viewParams.tab.value !== result.selectedTab) {
                this.viewParams.tab.value = result.selectedTab;
                this.lastTab = result.selectedTab;
            }
        });
    }

    public dispose() {
        if (this.tabChangeReactionDisposer) this.tabChangeReactionDisposer();
    }

    public async viewParamsUpdated() {
        if (this.viewParams.userGroup.value) {
            await this.viewModel.loadUserGroup(this.viewParams.userGroup.asNullableInt()!);

            if (this.viewModel.userGroup.userGroupId === this.viewParams.userGroup.asNullableInt()) {
                // set the description for the crumb trail
                this.viewParams.userGroup.description = this.viewModel.userGroup.userGroupName;

                if (this.viewParams.tab.value) {
                    this.viewModel.selectedTab = this.viewParams.tab.description = this.viewParams.tab.asString();
                } else {
                    this.viewParams.tab.value = this.lastTab;
                }
            } else {
                // nothing found, so clear the view param
                this.viewParams.userGroup.value = null;
            }
        } else {
            this.viewModel.viewState = UserGroupsViewState.ManageList;
        }
    }

    private authorisationService = Misc.Globals.appService.get(AppServices.NeoTypes.Security.AuthorisationService);

    private canUpdateUserGroupName() {
        return this.viewModel.userGroup.isAdministratorGroup;
    }

    private canDeleteUserGroup() {
        return this.viewModel.authorisationService.hasRole(Users.InviteUser);
    }

    private back() {
        this.viewParams.userGroup.value = null;
        this.viewModel.viewState = UserGroupsViewState.ManageList;
    }

    private manageUserGroup(item: UserGroupLookup) {
        this.viewParams.userGroup.value = item.userGroupId;
    }

    public render() {
        return (
            <React.Fragment>
                <div className="UserGroupsView">
                    {/* UPPER BUTTONS */}
                    <div className="row">
                        <div className="col-11 col-md-10">
                            {(this.viewModel.viewState !== UserGroupsViewState.ManageList) &&
                                <div>
                                    <Neo.Button className="mt-3" variant={'primary'} isOutline
                                        onClick={() => this.back()} >
                                        {TextConstants.Buttons.Back}
                                    </Neo.Button>
                                </div>}
                        </div>
                    </div>
                    {this.viewModel.viewState === UserGroupsViewState.ManageList &&
                        <Neo.Card title="User Groups">
                            <Neo.Button variant={'primary'}
                                onClick={() => this.viewModel.addUserGroup()}>
                                {TextConstants.Buttons.AddNewGroup}
                            </Neo.Button>
                            <div className="manage-user-group-list mt-4">
                                {/* LOADING MESSAGE */}
                                {!this.viewModel.userGroupsLoaded && <div className="loading">
                                    <h5>{TextConstants.GeneralText.Loading}</h5> </div>}
                                {/* NO USER GROUPS MESSAGE */}
                                {this.viewModel.userGroupsLoaded && this.viewModel.userGroups.length === 0 &&
                                    <div className="add-first-message">
                                        {!this.viewModel.userGroup.isAdministratorGroup &&
                                            <h5>{TextConstants.GeneralText.NoUserGroups}</h5>}
                                        {this.viewModel.userGroup.isAdministratorGroup &&
                                            <h5>{TextConstants.GeneralText.NoUserGroupsSecurity}</h5>}
                                    </div>}
                                {/* USER GROUPS GRID */}
                                {this.viewModel.userGroups.length > 0 &&
                                    <div className="user-groups-grid">
                                        <NeoGrid.Grid
                                            items={this.viewModel.userGroups} >
                                            {item => (
                                                <NeoGrid.Row>
                                                    <NeoGrid.Column display={item.meta.userGroupName} />
                                                    <NeoGrid.Column display={item.meta.memberCount} />
                                                    <NeoGrid.Column display={item.meta.roleCountDisplay} className="number-col" />
                                                    <NeoGrid.ButtonColumn>
                                                        <Neo.Button className="btn-small" variant="primary" isOutline icon="edit" onClick={() => this.manageUserGroup(item)} >
                                                            <span className="d-none d-lg-inline">{this.viewModel.userGroup.isAdministratorGroup ? "" : ""}</span>
                                                        </Neo.Button>
                                                        {this.canDeleteUserGroup() &&
                                                            <Neo.Button variant="danger" icon="trash" onClick={() => this.viewModel.deleteUserGroup(item)} >
                                                                <span className="d-none d-lg-inline"></span>
                                                            </Neo.Button>}
                                                    </NeoGrid.ButtonColumn>
                                                </NeoGrid.Row>
                                            )}
                                        </NeoGrid.Grid>
                                    </div>}
                            </div>
                        </Neo.Card>
                    }
                    {/* MANAGE USER GROUP */}
                    {this.viewModel.viewState === UserGroupsViewState.ManageItem &&
                        <Neo.Card title={this.viewModel.userGroup.isNew ? "New User Group:" : "User Group: " + this.viewModel.userGroup.userGroupName} >
                            <div className="manage-user-group">
                                <Neo.Form model={this.viewModel.userGroup}
                                    onSubmit={() => this.viewModel.save()} >
                                    <div className="row">
                                        <div className="col-6">
                                            <Neo.FormGroup
                                                bind={this.viewModel.userGroup.meta.userGroupName}
                                                editorProps={{
                                                    readOnly: !this.canUpdateUserGroupName(),
                                                    autoFocus: this.viewModel.userGroup.isNew,
                                                    placeholder: "New user group"
                                                }} />
                                        </div>
                                        <div className="col-6 ">
                                            {this.authorisationService.hasRole(Roles.UserAccess.Update) &&
                                                <Neo.Button

                                                    isSubmit
                                                    className={this.viewModel.userGroup.isDirty || this.viewModel.resourceRoles.isDirty ? "primary-color-pulse mt-5 pull-right" : ""}
                                                    disabled={this.viewModel.canSave()}
                                                    variant="primary" >
                                                    <span className="d-none d-lg-inline">{TextConstants.Buttons.Save}</span>
                                                </Neo.Button>}
                                        </div>
                                    </div>
                                </Neo.Form>
                                {!this.viewModel.userGroup.isNew &&
                                    <Neo.TabContainer selectedTab={this.viewModel.meta.selectedTab}>

                                        {/* GROUP MEMBERS */}
                                        <Neo.Tab header={"Members (" + this.viewModel.groupMemberships.length + ")"} icon="users" name={TextConstants.Titles.Members} >
                                            <div className="group-members">
                                                {/* ADD MEMBER */}
                                                    {<Neo.Card className="ml-0 mr-0 mt-0" icon="fa-users" title={TextConstants.Titles.Members}>
                                                        <div className="col-4 p-0">
                                                            <Neo.FormGroup bind={this.viewModel.memberFindCriteria.meta.addUserId}
                                                                select={{
                                                                    items: this.viewModel.userList,
                                                                    displayMember: "nameAndEmail",
                                                                    valueMember: "userId"
                                                                }} />
                                                        </div>
                                                        <div className="col-2 p-0">
                                                            <Neo.Button variant="primary" icon="user-plus"
                                                                disabled={this.viewModel.isSaving}
                                                                onClick={() => this.viewModel.addMember()} >
                                                                <span className="d-none d-lg-inline">{TextConstants.Buttons.Add}</span>
                                                            </Neo.Button>
                                                        </div>
                                                    </Neo.Card>}
                                                {/* MEMBER LIST */}
                                                <Neo.Card className="ml-0 mr-0" title={TextConstants.Titles.Members} >
                                                    {this.viewModel.groupMemberships.length > 0 &&
                                                        <NeoGrid.Grid
                                                            items={this.viewModel.groupMemberships} >
                                                            {item => (
                                                                <NeoGrid.Row>
                                                                    <NeoGrid.Column display={item.meta.memberName} />
                                                                    <NeoGrid.Column display={item.meta.userName} />
                                                                    <NeoGrid.ButtonColumn hidden={this.viewModel.userGroup.isAdministratorGroup && !this.authorisationService.hasRole(Users.InviteUser)}>
                                                                        <Neo.Button variant="danger" icon="user-times"
                                                                            onClick={() => this.viewModel.removeMember(item)} >
                                                                            <span className="d-none d-lg-inline">{TextConstants.Buttons.Remove}</span>
                                                                        </Neo.Button>
                                                                    </NeoGrid.ButtonColumn>
                                                                </NeoGrid.Row>)}
                                                        </NeoGrid.Grid>}
                                                </Neo.Card>
                                            </div>

                                        </Neo.Tab>
                                        <Neo.Tab header={"Roles (" + this.viewModel.assignedRolesLength + ")"} icon="user-lock" name="Roles">
                                            <Neo.Card className="ml-0 mr-0 mt-0" title={TextConstants.Titles.Roles}>
                                                {/* LOADING ROLES MESSAGE */}
                                                {this.viewModel.rolesTask.isBusy &&
                                                    <div className="add-first-message"> <h4>Loading available roles...</h4> </div>}
                                                {/* NO ROLES MESSAGE */}
                                                {!this.viewModel.rolesTask.isBusy && this.viewModel.resourceRoles.length === 0 &&
                                                    <div className="add-first-message"> <h4>No roles available, add authorisation to your resources.</h4> </div>}
                                                <div className="row">
                                                    <div className="group-roles">
                                                        <Neo.Loader task={this.viewModel.rolesTask}>            
                                                            {/*!this.viewModel.userGroup.isAdministratorGroup  -  Removed from enabled due to inconsistencies with required outcome*/}
                                                            <ResourceRoleTree items={this.viewModel.resourceRoles} enabled={true} /> 
                                                        </Neo.Loader>
                                                    </div>
                                                    <div className="role-tools">
                                                        <Neo.Button size="sm" icon="undo" data-tip="Undo role changes" hidden={!this.viewModel.resourceRoles.isDirty}
                                                            onClick={() => this.viewModel.resetAssignedRoles()} >

                                                        </Neo.Button>
                                                    </div>

                                                </div>

                                            </Neo.Card>
                                        </Neo.Tab>
                                    </Neo.TabContainer>}
                            </div>
                        </Neo.Card>
                    }
                </div>
            </React.Fragment>
        )
    }
}